import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inspirationalAPI, uploadAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronLeft,
  Heart,
  MessageCircle,
  Send,
  Trash2,
  Loader2,
  Youtube,
  Link as LinkIcon,
  Plus,
  X,
  Reply,
  ImagePlus
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

// Helper to get video platform icon color
const getVideoTypeColor = (type) => {
  switch (type) {
    case 'youtube': return 'text-red-600';
    case 'facebook': return 'text-blue-600';
    case 'instagram': return 'text-pink-600';
    case 'tiktok': return 'text-black';
    default: return 'text-gray-600';
  }
};

const InspirationalPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostVideoUrl, setNewPostVideoUrl] = useState('');
  const [attachments, setAttachments] = useState([]); // [{url, type:'image'|'video', name}]
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [submittingComment, setSubmittingComment] = useState(null);
  const [replyingTo, setReplyingTo] = useState({});

  useEffect(() => {
    loadPosts();
    inspirationalAPI.markVisited().catch(() => {});
  }, []);

  const loadPosts = async () => {
    try {
      const data = await inspirationalAPI.getPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !newPostVideoUrl.trim() && attachments.length === 0) return;
    
    setSubmitting(true);
    try {
      const media = attachments.map(a => ({ url: a.url, type: a.type }));
      const newPost = await inspirationalAPI.createPost(newPostContent, newPostVideoUrl, media);
      setPosts([newPost, ...posts]);
      setNewPostContent('');
      setNewPostVideoUrl('');
      setAttachments([]);
      setShowNewPost(false);
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        if (!isVideo && !isImage) {
          alert(`"${file.name}" is not a photo or video — skipped.`);
          continue;
        }
        const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
        if (file.size > maxSize) {
          const maxMB = maxSize / (1024 * 1024);
          const fileMB = (file.size / (1024 * 1024)).toFixed(1);
          alert(`"${file.name}" is too large (${fileMB}MB). Max ${isVideo ? 'video' : 'photo'} size is ${maxMB}MB — skipped.`);
          continue;
        }
        setUploadProgress(0);
        const result = await uploadAPI.uploadFile(file, (progress) => setUploadProgress(progress));
        setAttachments(prev => [...prev, { url: result.url, type: isVideo ? 'video' : 'image', name: file.name }]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to upload: ${errorMsg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleToggleCommentLike = async (postId, commentId) => {
    try {
      const result = await inspirationalAPI.toggleCommentLike(postId, commentId);
      setPosts(posts.map(post =>
        post.id === postId
          ? {
              ...post,
              comments: post.comments.map(c =>
                c.id === commentId ? { ...c, likes: result.likes, likeCount: result.likeCount } : c
              )
            }
          : post
      ));
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      const result = await inspirationalAPI.toggleLike(postId);
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, likes: result.likes, likeCount: result.likeCount }
          : post
      ));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleAddComment = async (postId) => {
    const content = commentText[postId]?.trim();
    if (!content) return;
    
    setSubmittingComment(postId);
    try {
      const reply = replyingTo[postId];
      const replyPayload = reply
        ? { id: reply.id, userName: reply.userName, content: (reply.content || '').slice(0, 150) }
        : null;
      const newComment = await inspirationalAPI.addComment(postId, content, replyPayload);
      setPosts(posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              comments: [...post.comments, newComment],
              commentCount: post.commentCount + 1
            }
          : post
      ));
      setCommentText({ ...commentText, [postId]: '' });
      setReplyingTo({ ...replyingTo, [postId]: null });
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(null);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await inspirationalAPI.deleteComment(postId, commentId);
      setPosts(posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              comments: post.comments.filter(c => c.id !== commentId),
              commentCount: post.commentCount - 1
            }
          : post
      ));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post and all its comments?')) return;
    
    try {
      await inspirationalAPI.deletePost(postId);
      setPosts(posts.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const renderVideoEmbed = (post) => {
    if (!post.videoUrl) return null;
    
    // Directly uploaded video file -> native player
    if (post.videoType === 'upload') {
      return (
        <div className="rounded-xl overflow-hidden bg-black mt-3">
          <video src={post.videoUrl} controls className="w-full max-h-96" data-testid={`post-video-${post.id}`} />
        </div>
      );
    }
    
    const youtubeId = getYouTubeId(post.videoUrl);
    if (youtubeId) {
      return (
        <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden bg-gray-100 mt-3">
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    
    // For other video types, show a link card
    return (
      <a
        href={post.videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 mt-3 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
      >
        <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center ${getVideoTypeColor(post.videoType)}`}>
          {post.videoType === 'youtube' ? <Youtube className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-navy-900 capitalize">{post.videoType || 'Video'} Link</p>
          <p className="text-xs text-gray-500 truncate">{post.videoUrl}</p>
        </div>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-4 pt-6">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/')} className="p-1 hover:bg-white/20 rounded-full">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Inspirational</h1>
            <p className="text-red-100 text-sm">Share uplifting content with your church family</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* New Post Button */}
        {!showNewPost && (
          <Button
            onClick={() => setShowNewPost(true)}
            className="w-full bg-white text-red-500 border-2 border-red-500 hover:bg-red-50 rounded-xl py-6"
            data-testid="new-inspirational-post-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Share Something Inspirational
          </Button>
        )}

        {/* New Post Form */}
        {showNewPost && (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-navy-900">Share Inspiration</h3>
                <button onClick={() => setShowNewPost(false)} className="p-1 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Write an inspirational message... (feel free to write as much as you'd like)"
                className="w-full p-3 border border-gray-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={6}
                data-testid="inspirational-post-content"
              />
              
              <div className="mt-3">
                <label className="text-sm text-gray-600 mb-1 block">Video Link (optional)</label>
                <Input
                  value={newPostVideoUrl}
                  onChange={(e) => setNewPostVideoUrl(e.target.value)}
                  placeholder="Paste YouTube, Facebook, TikTok, or Instagram link"
                  className="rounded-xl"
                  data-testid="inspirational-post-video"
                />
              </div>

              {/* Upload Photos / Videos */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*,video/*"
                multiple
                className="hidden"
                data-testid="inspirational-file-input"
              />
              {attachments.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid="inspirational-attachment-preview">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="relative rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-100">
                      {att.type === 'image' ? (
                        <img src={att.url} alt="Attachment" className="w-full h-full object-cover" />
                      ) : (
                        <video src={att.url} className="w-full h-full object-cover bg-black" />
                      )}
                      {att.type === 'video' && (
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1 rounded">VIDEO</span>
                      )}
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80"
                        data-testid={`remove-attachment-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-red-400 hover:text-red-500 transition-colors"
                data-testid="add-media-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Uploading... {uploadProgress}%</span>
                  </>
                ) : (
                  <>
                    <ImagePlus className="w-5 h-5" />
                    <span>{attachments.length > 0 ? 'Add More Photos or Videos' : 'Add Photos or Videos'}</span>
                  </>
                )}
              </button>
              
              <Button
                onClick={handleCreatePost}
                disabled={submitting || uploading || (!newPostContent.trim() && !newPostVideoUrl.trim() && attachments.length === 0)}
                className="w-full mt-3 bg-red-500 hover:bg-red-600 text-white rounded-xl"
                data-testid="submit-inspirational-post"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Share
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Posts List */}
        {posts.length === 0 ? (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-8 text-center">
              <Heart className="w-12 h-12 text-red-200 mx-auto mb-3" />
              <h3 className="font-semibold text-navy-900 mb-1">No Inspirational Posts Yet</h3>
              <p className="text-gray-500 text-sm">Be the first to share something uplifting!</p>
            </CardContent>
          </Card>
        ) : (
          posts.map(post => (
            <Card key={post.id} className="border-0 shadow-lg rounded-2xl overflow-hidden" data-testid={`inspirational-post-${post.id}`}>
              <CardContent className="p-4">
                {/* Post Header */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-pink-400 flex items-center justify-center text-white font-bold">
                    {post.userAvatar ? (
                      <img src={post.userAvatar} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      post.userName?.charAt(0)?.toUpperCase() || '?'
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-navy-900">{post.userName}</p>
                        <p className="text-xs text-gray-500">{formatRelativeTime(post.createdAt)}</p>
                      </div>
                      {(post.userId === user?.id || user?.role === 'admin' || user?.role === 'pastor') && (
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                          title="Delete post"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                {post.content && (
                  <p className="mt-3 text-navy-900 whitespace-pre-wrap">{post.content}</p>
                )}

                {/* Media Gallery (photos + uploaded videos) */}
                {post.media && post.media.length > 0 && (
                  <div className={`mt-3 grid gap-2 ${post.media.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {post.media.map((m, idx) => (
                      m.type === 'video' ? (
                        <div key={idx} className="rounded-xl overflow-hidden bg-black" data-testid={`post-video-${post.id}-${idx}`}>
                          <video src={m.url} controls className="w-full max-h-96" />
                        </div>
                      ) : (
                        <div key={idx} className="rounded-xl overflow-hidden bg-gray-100" data-testid={`post-image-${post.id}-${idx}`}>
                          <img src={m.url} alt="Post" className="w-full max-h-96 object-contain" />
                        </div>
                      )
                    ))}
                  </div>
                )}
                {/* Legacy single image (old posts) */}
                {(!post.media || post.media.length === 0) && post.imageUrl && (
                  <div className="rounded-xl overflow-hidden bg-gray-100 mt-3">
                    <img src={post.imageUrl} alt="Post" className="w-full max-h-96 object-contain" data-testid={`post-image-${post.id}`} />
                  </div>
                )}

                {/* External Video Link Embed */}
                {renderVideoEmbed(post)}

                {/* Actions */}
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleToggleLike(post.id)}
                    className={`flex items-center gap-1 ${post.likes?.includes(user?.id) ? 'text-red-500' : 'text-gray-500'} hover:text-red-500 transition-colors`}
                    data-testid={`like-btn-${post.id}`}
                  >
                    <Heart className={`w-5 h-5 ${post.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                    <span className="text-sm font-medium">{post.likeCount || 0}</span>
                  </button>
                  <button
                    onClick={() => toggleComments(post.id)}
                    className="flex items-center gap-1 text-gray-500 hover:text-blue-500 transition-colors"
                    data-testid={`comment-btn-${post.id}`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">{post.commentCount || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {expandedComments[post.id] && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    {/* Existing Comments */}
                    {post.comments?.length > 0 && (
                      <div className="space-y-3 mb-3">
                        {post.comments.map(comment => (
                          <div key={comment.id} className="flex gap-2" data-testid={`comment-${comment.id}`}>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                              {comment.userAvatar ? (
                                <img src={comment.userAvatar} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                comment.userName?.charAt(0)?.toUpperCase() || '?'
                              )}
                            </div>
                            <div className="flex-1 bg-gray-100 rounded-xl p-2">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold text-navy-900">{comment.userName}</p>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setReplyingTo({ ...replyingTo, [post.id]: { id: comment.id, userName: comment.userName, content: comment.content } });
                                    }}
                                    className="p-1 text-gray-400 hover:text-blue-500 rounded-full"
                                    title="Reply"
                                    data-testid={`reply-comment-${comment.id}`}
                                  >
                                    <Reply className="w-3 h-3" />
                                  </button>
                                  {(comment.userId === user?.id || user?.role === 'admin' || user?.role === 'pastor') && (
                                    <button
                                      onClick={() => handleDeleteComment(post.id, comment.id)}
                                      className="p-1 text-gray-400 hover:text-red-500 rounded-full"
                                      title="Delete comment"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {comment.replyTo && (
                                <div className="mb-1 pl-2 border-l-2 border-blue-300 bg-white rounded-r-md py-1" data-testid={`comment-reply-quote-${comment.id}`}>
                                  <p className="text-xs font-semibold text-blue-600">{comment.replyTo.userName}</p>
                                  <p className="text-xs text-gray-500 truncate">{comment.replyTo.content}</p>
                                </div>
                              )}
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <p className="text-xs text-gray-400">{formatRelativeTime(comment.createdAt)}</p>
                                <button
                                  onClick={() => handleToggleCommentLike(post.id, comment.id)}
                                  className={`flex items-center gap-1 text-xs transition-colors ${
                                    comment.likes?.includes(user?.id) ? 'text-red-500 font-semibold' : 'text-gray-400 hover:text-red-500'
                                  }`}
                                  data-testid={`like-comment-${comment.id}`}
                                >
                                  <Heart className={`w-3.5 h-3.5 ${comment.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                                  {comment.likeCount > 0 && <span data-testid={`comment-like-count-${comment.id}`}>{comment.likeCount}</span>}
                                  <span>Like</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Comment */}
                    {replyingTo[post.id] && (
                      <div className="flex items-center gap-2 bg-blue-50 rounded-xl px-3 py-2 mb-2 border-l-4 border-blue-400" data-testid={`comment-reply-preview-${post.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-blue-600">Replying to {replyingTo[post.id].userName}</p>
                          <p className="text-xs text-gray-500 truncate">{replyingTo[post.id].content}</p>
                        </div>
                        <button
                          onClick={() => setReplyingTo({ ...replyingTo, [post.id]: null })}
                          className="p-1 text-gray-400 hover:text-red-500"
                          data-testid={`cancel-comment-reply-${post.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-tan-400 to-tan-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {user?.profileImage ? (
                          <img src={user.profileImage} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          user?.firstName?.charAt(0)?.toUpperCase() || '?'
                        )}
                      </div>
                      <div className="flex-1 flex gap-2">
                        <textarea
                          value={commentText[post.id] || ''}
                          onChange={(e) => {
                            setCommentText({ ...commentText, [post.id]: e.target.value });
                            // Auto-resize
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (commentText[post.id]?.trim()) {
                                handleAddComment(post.id);
                              }
                            }
                          }}
                          placeholder="Write a comment... (Shift+Enter for new line)"
                          className="flex-1 rounded-xl text-sm px-3 py-2 border border-gray-200 resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-red-300"
                          style={{ minHeight: '40px', maxHeight: '100px' }}
                          rows={1}
                          data-testid={`comment-input-${post.id}`}
                        />
                        <Button
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentText[post.id]?.trim() || submittingComment === post.id}
                          size="icon"
                          className="rounded-full bg-red-500 hover:bg-red-600 flex-shrink-0"
                          data-testid={`submit-comment-${post.id}`}
                        >
                          {submittingComment === post.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default InspirationalPage;
